import { useState, type FormEvent } from 'react'
import { treasuryScenariosApi, type EconomicSimulatorInput, type EconomicSimulatorResult } from '../api/treasuryScenarios'
import { eur, errMsg } from '../lib/financeFormat'
import {
  Button, Callout, Card, CardContent, CardHeader, ChartCard, DataTable, FormGrid, LineChart, StatusPill, TextField,
} from './ui'

/**
 * Blocco 1 / Card D (REQ-195-001) — simulatore economico: dati ipotetici (produzione mensile, margine, costi, provvigioni)
 * e orizzonte in mesi; il calcolo e' puro sul server, nessun salvataggio. Le imposte sono SEMPRE una stima: l'etichetta
 * (STIMATO) resta visibile accanto alla colonna e al totale (BLK-003). Si monta dietro il flag `finanza.simulatore`.
 */

interface FormState {
  monthlyProduction: string
  marginPct: string
  fixedCosts: string
  variableCosts: string
  commissions: string
  months: string
}

const INITIAL: FormState = { monthlyProduction: '', marginPct: '', fixedCosts: '', variableCosts: '0', commissions: '0', months: '12' }

function validate(f: FormState): string | null {
  const nonNeg = (v: string) => v !== '' && Number.isFinite(Number(v)) && Number(v) >= 0
  if (!nonNeg(f.monthlyProduction)) return 'Indica la produzione mensile.'
  if (!(f.marginPct !== '' && Number(f.marginPct) >= 0 && Number(f.marginPct) <= 100)) return 'Il margine deve essere tra 0 e 100%.'
  if (!nonNeg(f.fixedCosts) || !nonNeg(f.variableCosts) || !nonNeg(f.commissions)) return 'I costi non possono essere negativi.'
  const m = Number(f.months)
  if (!Number.isInteger(m) || m < 1 || m > 60) return 'L’orizzonte va da 1 a 60 mesi.'
  return null
}

export function EconomicSimulatorPanel() {
  const [f, setF] = useState<FormState>(INITIAL)
  const [touched, setTouched] = useState(false)
  const [result, setResult] = useState<EconomicSimulatorResult | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const problem = validate(f)
  const set = (k: keyof FormState, v: string) => setF(prev => ({ ...prev, [k]: v }))

  async function run(e: FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (problem) return
    const body: EconomicSimulatorInput = {
      monthlyProduction: Number(f.monthlyProduction),
      marginPct: Number(f.marginPct),
      fixedCosts: Number(f.fixedCosts),
      variableCosts: Number(f.variableCosts),
      commissions: Number(f.commissions),
      months: Number(f.months),
    }
    setBusy(true)
    setError('')
    try {
      setResult(await treasuryScenariosApi.simulate(body))
    } catch (x) {
      setResult(null)
      setError(errMsg(x, 'Simulazione non riuscita.'))
    } finally {
      setBusy(false)
    }
  }

  const label = result?.taxesLabel ?? 'STIMATO'
  const cats = result ? result.months.map((_, i) => `M${i + 1}`) : []
  const hidden = result !== null && result.months.length > 0 && result.months.every(m => m.liquidity === undefined)

  return (
    <Card variant="outlined">
      <CardHeader title="Simulatore economico" subtitle="Ipotesi di scenario: nulla viene salvato. Le imposte sono una stima, non un calcolo fiscale." />
      <CardContent>
        <form onSubmit={e => void run(e)}>
          <FormGrid columns={3}>
            <TextField label="Produzione mensile" type="number" min={0} step="0.01" prefix="€" value={f.monthlyProduction} onChange={e => set('monthlyProduction', e.target.value)} />
            <TextField label="Margine" type="number" min={0} max={100} step="0.1" suffix="%" value={f.marginPct} onChange={e => set('marginPct', e.target.value)} />
            <TextField label="Costi fissi mensili" type="number" min={0} step="0.01" prefix="€" value={f.fixedCosts} onChange={e => set('fixedCosts', e.target.value)} />
            <TextField label="Costi variabili mensili" type="number" min={0} step="0.01" prefix="€" value={f.variableCosts} onChange={e => set('variableCosts', e.target.value)} />
            <TextField label="Provvigioni mensili" type="number" min={0} step="0.01" prefix="€" value={f.commissions} onChange={e => set('commissions', e.target.value)} />
            <TextField label="Orizzonte" type="number" min={1} max={60} step={1} suffix="mesi" value={f.months} onChange={e => set('months', e.target.value)} />
          </FormGrid>
          {touched && problem && <Callout tone="warning">{problem}</Callout>}
          <Button variant="filled" type="submit" icon="calculate" loading={busy}>Simula</Button>
        </form>

        {error && <Callout tone="error">{error}</Callout>}

        {result && (
          <>
            <ChartCard title="Liquidità simulata" subtitle="Liquidità totale e liquidità libera, mese per mese">
              <LineChart
                label="Liquidità simulata"
                categoryHeading="Mese"
                unit="eur"
                categories={cats}
                series={[
                  { id: 'liquidity', name: 'Liquidità', color: 'primary', values: result.months.map(m => m.liquidity ?? null) },
                  { id: 'free', name: 'Liquidità libera', color: 'success', values: result.months.map(m => m.freeLiquidity ?? null) },
                ]}
                emptyTitle="Nessun dato"
                emptyDescription={hidden ? 'Gli importi non sono visibili con il tuo profilo.' : undefined}
              />
            </ChartCard>
            <DataTable variant="plain" aria-label="Risultato della simulazione">
              <thead>
                <tr>
                  <th>Mese</th><th className="text-end">Margine</th><th className="text-end">Flusso di cassa</th>
                  <th className="text-end">Imposte <StatusPill tone="warning">{label}</StatusPill></th>
                  <th className="text-end">Liquidità</th><th className="text-end">Liquidità libera</th>
                </tr>
              </thead>
              <tbody>
                {result.months.map((m, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td className="text-end md-num">{eur(m.margin)}</td>
                    <td className="text-end md-num">{eur(m.cashFlow)}</td>
                    <td className="text-end md-num">{eur(m.taxesEstimated)}</td>
                    <td className="text-end md-num">{eur(m.liquidity)}</td>
                    <td className="text-end md-num">{eur(m.freeLiquidity)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </>
        )}
      </CardContent>
    </Card>
  )
}
